import { useCallback, useMemo } from 'react'

import type { CartItemPriceSnapshot, ResolvedCatalogue, ResolvedShellProduct, ResolvedVariant, SalesforceId } from 'models/purchaseFlowRevamp'

import { PfRevampEventName, usePfRevampAnalytics } from '../../analytics'
import { EMPTY_PACK_MEMBER_ADDONS, usePfStore } from '../../store'
import { replacesExistingLine } from '../../store/documentSelectionPolicy'
import { lineTotalOf } from '../../store/lineTotal'
import { isPack, packCartLines } from '../../store/packLines'
import { quotePackMember } from './packMemberQuote'
import type { ProductCommentSurface } from './productComment'
import { aiEditingToCarry, noteToCarry } from './uploadRequirement'
import type { DrawerPricing } from './useDrawerPricing'
import type { useDrawerUploads } from './useDrawerUploads'

interface DrawerCommitInput {
  shell: ResolvedShellProduct | null
  selectedVariant: ResolvedVariant | undefined
  pricing: DrawerPricing
  uploads: Pick<ReturnType<typeof useDrawerUploads>, 'showUploadSection' | 'ownUploadedFiles'>
  commentSurface: ProductCommentSurface
  isMultiSelectDocuments: boolean
  /** Every ticked document, primary first. */
  tickedVariantIds: SalesforceId[]
  /** Where a pack's members are looked up. */
  catalogue?: ResolvedCatalogue
}

/**
 * The drawer's "Add" / "Save changes": writes the pending selection to the cart as one line (or one
 * per ticked document), stores the line's uploads, and closes the drawer.
 *
 * `replaceItem` rebuilds the whole line, so settings the drawer does not author — the AI settings and,
 * when the configuration page owns the box, the brief — are carried over from the line being edited.
 */
export const useDrawerCommit = ({
  shell,
  selectedVariant,
  pricing,
  uploads,
  commentSurface,
  isMultiSelectDocuments,
  tickedVariantIds,
  catalogue,
}: DrawerCommitInput) => {
  const mode = usePfStore((s) => s.mode)
  const editingCartItemId = usePfStore((s) => s.editingCartItemId)
  const shellProductId = usePfStore((s) => s.shellProductId)
  const selectedVariantId = usePfStore((s) => s.selectedVariantId)
  const optionValues = usePfStore((s) => s.optionValues)
  const selectedExtraIds = usePfStore((s) => s.selectedExtraIds)
  const extraQuantities = usePfStore((s) => s.extraQuantities)
  const quantity = usePfStore((s) => s.quantity)
  const note = usePfStore((s) => s.note)
  const unselectedPackMemberCodes = usePfStore((s) => s.unselectedPackMemberCodes)
  const packMemberAddons = usePfStore((s) => s.packMemberAddons)
  const cartItems = usePfStore((s) => s.items)
  const closeDrawer = usePfStore((s) => s.close)
  const addItem = usePfStore((s) => s.addItem)
  const replaceItem = usePfStore((s) => s.replaceItem)
  const removeItem = usePfStore((s) => s.removeItem)
  const setAttachments = usePfStore((s) => s.setAttachments)
  const trackAnalytics = usePfRevampAnalytics()

  const { showUploadSection, ownUploadedFiles } = uploads
  const isEdit = mode === 'edit' && !!editingCartItemId
  const editedLine = useMemo(() => cartItems.find((item) => item.id === editingCartItemId), [cartItems, editingCartItemId])

  /** Dropped when the newly selected variant does not announce the settings: they would describe a product no longer bought. */
  const keptAiEditing = useMemo(() => aiEditingToCarry({
    isEdit,
    existing: editedLine?.aiEditing,
    variantFlows: selectedVariant?.configurationFlows,
  }), [isEdit, editedLine, selectedVariant])

  const keptNote = useMemo(() => noteToCarry({
    isEdit,
    existing: editedLine?.note,
    surface: commentSurface,
  }), [isEdit, editedLine, commentSurface])

  /** The selection narrowed to what the committed variant offers: ticks left over from another variant are neither shown nor priced. */
  const offeredExtras = useMemo(() => {
    const offered = new Set(pricing.extras.map((extra) => extra.id))
    return {
      ids: selectedExtraIds.filter((id) => offered.has(id)),
      quantities: Object.fromEntries(Object.entries(extraQuantities).filter(([id]) => offered.has(id))),
    }
  }, [pricing.extras, selectedExtraIds, extraQuantities])

  /** Exactly what the drawer showed, since `serializePlacement` turns it into the line's `inputImages`. */
  const buildStagingAttachments = useCallback(
    () => ownUploadedFiles.map((file) => ({ kind: 'staging-visual' as const, ...file })),
    [ownUploadedFiles]
  )

  const commitNewItem = useCallback((
    payload: Parameters<typeof addItem>[0],
    // Only the last of several document lines closes the drawer, so it does not shut mid-loop.
    { closeAfter = true }: { closeAfter?: boolean } = {}
  ) => {
    const newCartItemId = addItem(payload)

    trackAnalytics({
      type: PfRevampEventName.CART_ITEM_ADDED,
      payload: {
        shellProductId: payload.shellProductId,
        variantId: payload.variantId,
        qty: payload.quantity,
        priceTotal: Number(lineTotalOf(payload.priceSnapshot!, payload.quantity).toString()),
        currency: payload.priceSnapshot!.currency,
      },
    })

    if (showUploadSection && ownUploadedFiles.length > 0) {
      setAttachments(newCartItemId, buildStagingAttachments())
    }

    if (closeAfter) closeDrawer()
  }, [addItem, buildStagingAttachments, closeDrawer, ownUploadedFiles.length, setAttachments, showUploadSection, trackAnalytics])

  return useCallback(() => {
    if (!selectedVariantId || !shellProductId || !pricing.isPriced || !pricing.unitPrice || !pricing.currency) {
      return
    }

    // A pack is never a line itself: its members go in as ordinary lines, removed together.
    if (isPack(selectedVariant) && selectedVariant && !(mode === 'edit' && editingCartItemId)) {
      const lines = packCartLines(selectedVariant, catalogue, crypto.randomUUID(), new Date().toISOString(), unselectedPackMemberCodes,
        (member) => quotePackMember(catalogue, member, packMemberAddons[member.productCode] ?? EMPTY_PACK_MEMBER_ADDONS)?.lineAddons ?? null)
      if (lines.length === 0) return
      lines.forEach((line) => addItem(line))
      trackAnalytics({
        type: PfRevampEventName.PACK_ADDED,
        payload: {
          packProductCode: selectedVariant.productCode,
          lineCount: lines.length,
          priceTotal: lines.reduce((sum, line) => sum + Number(line.priceSnapshot?.unitPrice ?? 0) + Number(line.priceSnapshot?.addonsPrice ?? 0), 0),
          currency: pricing.currency,
        },
      })
      closeDrawer()
      return
    }

    const snapshot: CartItemPriceSnapshot = {
      unitPrice: pricing.unitPrice.toFixed(2),
      addonsPrice: pricing.addonsPrice?.gt(0) ? pricing.addonsPrice.toFixed(2) : null,
      // Taken with `unitPrice`, so the struck and the charged figure come from one catalogue read.
      baseUnitPrice: pricing.baseUnitPrice?.toFixed(2) ?? null,
      currency: pricing.currency,
      resolvedAt: new Date().toISOString(),
    }

    const committedNote = commentSurface === 'drawer' ? note.trim() : keptNote

    const payload = {
      shellProductId,
      variantId: selectedVariantId,
      quantity,
      optionValues,
      extraServiceIds: offeredExtras.ids,
      extraQuantities: offeredExtras.quantities,
      priceSnapshot: snapshot,
      ...(shell?.displayName ? { productName: shell.displayName } : {}),
      ...(keptAiEditing ? { aiEditing: keptAiEditing } : {}),
      // Omitted when blank, so clearing the box removes the note instead of ordering an empty comment.
      ...(committedNote ? { note: committedNote } : {}),
    }

    if (mode === 'edit' && editingCartItemId) {
      replaceItem(editingCartItemId, payload)
      if (showUploadSection) setAttachments(editingCartItemId, buildStagingAttachments())
      closeDrawer()
      return
    }

    // A single-selection document group holds one line per property, so re-adding replaces it.
    if (replacesExistingLine(shell)) {
      cartItems
        .filter((item) => item.shellProductId === shellProductId)
        .forEach((item) => removeItem(item.id))
    }

    // One line per ticked document, each at its own catalogue price; a document group offers no
    // options, extras or uploads, so nothing else is carried.
    if (isMultiSelectDocuments && tickedVariantIds.length > 1) {
      tickedVariantIds.forEach((variantId, index) => {
        const variant = shell?.variants.find((candidate) => candidate.id === variantId)
        if (!variant?.price) return

        commitNewItem({
          ...payload,
          variantId,
          quantity: 1,
          optionValues: {},
          extraServiceIds: [],
          extraQuantities: {},
          priceSnapshot: {
            unitPrice: variant.price.unitPrice,
            currency: variant.price.currency,
            resolvedAt: snapshot.resolvedAt,
          },
        }, { closeAfter: index === tickedVariantIds.length - 1 })
      })
      return
    }

    commitNewItem(payload)
  }, [
    selectedVariant,
    catalogue,
    unselectedPackMemberCodes,
    packMemberAddons,
    addItem,
    trackAnalytics,
    selectedVariantId,
    shellProductId,
    pricing.isPriced,
    pricing.unitPrice,
    pricing.addonsPrice,
    pricing.baseUnitPrice,
    pricing.currency,
    quantity,
    note,
    commentSurface,
    keptNote,
    optionValues,
    offeredExtras,
    mode,
    editingCartItemId,
    replaceItem,
    closeDrawer,
    showUploadSection,
    keptAiEditing,
    setAttachments,
    buildStagingAttachments,
    commitNewItem,
    isMultiSelectDocuments,
    tickedVariantIds,
    shell,
    cartItems,
    removeItem,
  ])
}
